import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { Search, Check, AlertTriangle, X, ExternalLink, Video, HelpCircle, Info, Phone } from 'lucide-react'
import { put } from '@vercel/blob/client'
import { api } from '@/lib/api'
import { extractVideoFrames } from '@/lib/videoFrames'
import { resolveSite } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { useUi, type UiStrings } from '@/lib/i18n/ui'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'

export const Route = createFileRoute('/tools/scam-checker')({
  component: ScamCheckerPage,
})

/**
 * Public scam checker. Paste a link or a message; the granguard-api engine
 * (heuristics + Safe Browsing/PhishTank + AI judge with a deterministic
 * consensus) returns a verdict with evidence. Anonymous and rate-limited
 * server-side; see /api/v1/public/scam-check.
 *
 * Styling matches the password/charity tool pages (Lee's prototype: shield
 * hero, card inputs, tinted result cards, violet for blue).
 */

type RedFlag = { severity: 'high' | 'medium' | 'low'; title: string; explanation: string; simplified: string; evidence?: string }
type ActionStep = { step: number; text: string; buttonLabel?: string; buttonUrl?: string }
/** "What we looked at": the engine restating the case before its verdict. URL checks only. */
type ObservationCheck = { key: string; status: 'clear' | 'concern' | 'unverified' | 'info'; title: string; detail: string }
type Observation = {
  summary: string
  claimedBrand: string | null
  brandVerification: { status: string; reference: string | null } | null
  checks: ObservationCheck[]
}
type CheckResult = {
  verdict: 'SAFE' | 'CAUTION' | 'DANGEROUS'
  headline: string
  explanation: string
  flags: RedFlag[]
  actions: ActionStep[]
  observation?: Observation | null
}

/**
 * Safety net for engine copy while granguard-api's wording fixes roll out:
 * no em dashes (house rule), no UK-only domain advice on US faces, and never
 * "search Google" to find an official contact (search ads lead to fake
 * support numbers). A no-op once the engine sends clean strings.
 */
function tidyEngineCopy(text: string, isTitle = false): string {
  return text
    .replace(/Most trusted websites end in \.com or \.co\.uk\./g, 'Most trusted websites use familiar endings like .com, .org or .gov.')
    .replace(/find their official website by searching for them on Google/g, 'use the phone number on your card or statement, or the app you already use')
    .replace(/look up their official details by searching for them on Google or checking the back of your bank card/g, 'use the phone number on the back of your card or on a statement')
    .replace(/\s*—\s*/g, isTitle ? ': ' : ', ')
}

function tidyResult(r: CheckResult): CheckResult {
  return {
    ...r,
    headline: tidyEngineCopy(r.headline ?? ''),
    explanation: tidyEngineCopy(r.explanation ?? ''),
    flags: (r.flags ?? []).map(f => ({
      ...f,
      title: tidyEngineCopy(f.title, true),
      explanation: tidyEngineCopy(f.explanation ?? ''),
      simplified: tidyEngineCopy(f.simplified ?? ''),
    })),
    actions: (r.actions ?? []).map(a => ({ ...a, text: tidyEngineCopy(a.text) })),
    observation: r.observation
      ? {
          ...r.observation,
          summary: tidyEngineCopy(r.observation.summary),
          checks: r.observation.checks.map(c => ({ ...c, title: tidyEngineCopy(c.title, true), detail: tidyEngineCopy(c.detail) })),
        }
      : r.observation,
  }
}

/**
 * Turn a failed request into a message the member can act on. The API's
 * 4xx bodies are not always JSON (see the architecture review, F16), so map
 * by status first and only peek at the body for "too long / too large".
 */
function errorMessage(e: any, t: UiStrings, kind: 'text' | 'file'): string {
  const res = e?.response
  if (!res) return e?.code === 'ECONNABORTED' ? t.scErrTimeout : kind === 'file' ? t.scErrFile : t.scErrGeneric
  const status: number = res.status
  const body = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '')
  if (status === 429) return t.scErrRate
  if (status === 413 || /too (long|large|big)|exceeds|max(imum)? (length|size)/i.test(body)) {
    return kind === 'file' ? t.scErrMediaSize : t.scErrTooLong
  }
  if (status === 415 || /unsupported|file type|mime/i.test(body)) return t.scErrType
  if (status === 408 || status === 504) return t.scErrTimeout
  if (status >= 400 && status < 500) return kind === 'file' ? t.scErrType : t.scErrBadInput
  return kind === 'file' ? t.scErrFile : t.scErrGeneric
}

const SHIELD = '[clip-path:polygon(50%_0,94%_14%,90%_66%,50%_100%,10%_66%,6%_14%)]'
const NAVY = 'text-[#221a3d]'

const looksLikeUrl = (s: string) => /^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(s.trim())

const VERDICT: Record<CheckResult['verdict'], { label: keyof UiStrings; card: string; icon: string; Icon: typeof Check }> = {
  SAFE: { label: 'scVerdictSafe', card: 'bg-[#f5fbf4] border-[#cfe8ca]', icon: 'bg-[#278a2f]', Icon: Check },
  CAUTION: { label: 'scVerdictCaution', card: 'bg-[#fff9ee] border-[#efddb2]', icon: 'bg-[#d08a12]', Icon: AlertTriangle },
  DANGEROUS: { label: 'scVerdictDanger', card: 'bg-[#fff5f4] border-[#f0c4bf]', icon: 'bg-[#c9382a]', Icon: X },
}

/** Ledger row styling: green = checked out, amber = stood out, grey = could not tell, violet = context. */
const CHECK_STATUS: Record<ObservationCheck['status'], { badge: string; Icon: typeof Check }> = {
  clear: { badge: 'bg-[#e6f4e4] text-[#278a2f]', Icon: Check },
  concern: { badge: 'bg-[#fdebd5] text-[#b4700a]', Icon: AlertTriangle },
  unverified: { badge: 'bg-slate-100 text-slate-500', Icon: HelpCircle },
  info: { badge: 'bg-violet-50 text-violet-600', Icon: Info },
}

function ScamCheckerPage() {
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [stage, setStage] = useState<string | null>(null)
  const [result, setResult] = useState<CheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const t = useUi()
  const locale = useLocale()
  // The engine answers in English; on the bilingual face we ask it to return
  // the verdict in the visitor's language and to route "report it" to the
  // Ministry of Interior rather than the FTC.
  const isQatar = resolveSite() === 'qatar'
  // Partner faces pass their slug so the engine can route "what to do" to
  // the partner's own contacts, looked up server-side (contract 2).
  const partnerSlug = COMMUNITY_PARTNER?.slug
  const engineOpts = {
    ...(isQatar ? { language: locale, region: 'QA' } : {}),
    ...(partnerSlug ? { partner: partnerSlug } : {}),
  }
  const [incomplete, setIncomplete] = useState(false)
  const [thin, setThin] = useState(false)

  /** Accept a result, or flag it as incomplete when the engine couldn't finish. */
  function accept(data: any) {
    if (data?.status === 'insufficient_input') { setThin(true); return }
    const done = data && typeof data === 'object' && data.verdict in VERDICT && data.status !== 'incomplete'
    if (!done) { setIncomplete(true); return }
    setResult(tidyResult(data as CheckResult))
  }

  async function run() {
    const value = input.trim()
    if (!value || busy) return
    const isUrl = looksLikeUrl(value)
    setBusy(true); setError(null); setResult(null); setIncomplete(false); setThin(false)
    setStage(isUrl ? t.scStageLink : t.scStageText)
    try {
      const { data } = await api.post('/public/scam-check', {
        input: value,
        inputType: isUrl ? 'URL' : 'TEXT',
        ...engineOpts,
      }, { timeout: 90_000 })
      accept(data)
    } catch (e: any) {
      setError(errorMessage(e, t, 'text'))
    } finally {
      setBusy(false); setStage(null)
    }
  }

  /** Media checks. Pictures go straight up as a form; video samples frames in
   *  the browser first; audio rides the video pipeline with no frames. */
  async function runMedia(file: File) {
    if (busy) return
    const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
    const isVideo = ['video/mp4', 'video/webm'].includes(file.type)
    const isAudio = ['audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav', 'audio/webm'].includes(file.type)
    if (!isImage && !isVideo && !isAudio) { setError(t.scErrType); return }
    if (isImage && file.size > 10 * 1024 * 1024) { setError(t.scErrImageSize); return }
    if (!isImage && file.size > 25 * 1024 * 1024) { setError(t.scErrMediaSize); return }
    setBusy(true); setError(null); setResult(null); setIncomplete(false); setThin(false)
    try {
      if (isImage) {
        setStage(t.scStageImage)
        const form = new FormData()
        form.append('image', file)
        if (isQatar) { form.append('language', locale); form.append('region', 'QA') }
        if (partnerSlug) form.append('partner', partnerSlug)
        const { data } = await api.post('/public/scam-check/image', form, { timeout: 150_000 })
        accept(data)
        return
      }
      setStage(isVideo ? t.scStageVideo : t.scStageAudio)
      const framesPromise = isVideo ? extractVideoFrames(file) : Promise.resolve({ frames: [] as string[], durationSec: null })
      const { data: tok } = await api.post('/public/scam-check/video/upload-token', {
        fileName: file.name, contentType: file.type, fileSize: file.size,
      }, { timeout: 30_000 })
      setStage(t.scStageUpload)
      const blob = await put(tok.pathname, file, { access: 'public', token: tok.token, contentType: file.type })
      const { frames, durationSec } = await framesPromise
      setStage(isVideo ? t.scStageVideoAnalyze : t.scStageAudioAnalyze)
      const { data } = await api.post('/public/scam-check/video', {
        videoUrl: blob.url, frames, fileName: file.name, durationSec, ...engineOpts,
      }, { timeout: 270_000 })
      accept(data)
    } catch (e: any) {
      setError(errorMessage(e, t, 'file'))
    } finally {
      setBusy(false); setStage(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="bg-gradient-to-b from-white to-[#fcfbff]">
      <main className="mx-auto max-w-[1180px] px-4 pb-10 pt-[22px] sm:px-7 sm:pb-16 sm:pt-[46px]">
        {/* Hero */}
        <section className="mb-7 grid grid-cols-1 items-center gap-4 sm:grid-cols-[72px_1fr] lg:grid-cols-[104px_1fr] lg:gap-[26px]">
          <div aria-hidden className={`hidden h-[72px] w-[64px] place-items-center bg-gradient-to-br from-violet-500 to-violet-800 text-white sm:grid lg:h-[98px] lg:w-[88px] ${SHIELD}`}>
            <Search className="h-8 w-8 -translate-y-[2px] lg:h-11 lg:w-11 lg:-translate-y-[3px]" strokeWidth={2.25} />
          </div>
          <div>
            <h1 className={`m-0 mb-[10px] text-[36px] font-bold leading-[1.03] tracking-[-1.4px] sm:text-[40px] lg:text-[54px] lg:tracking-[-2px] ${NAVY}`}>
              {t.scTitle}
            </h1>
            <p className="m-0 max-w-[760px] text-[16px] leading-[1.55] text-[#2f2a4a] sm:text-[18px]">
              {t.scIntro}
            </p>
            <div className="mt-[13px] flex items-center gap-2 text-[15px] font-bold text-violet-700">
              <Check className="h-4 w-4" strokeWidth={3} />
              <span>{t.scAssurance}</span>
            </div>
          </div>
        </section>

        {/* Input card */}
        <section className="mb-5 rounded-[18px] border border-[#e6e2f3] bg-white p-5 shadow-[0_10px_30px_rgba(56,23,112,.07)]">
          <label htmlFor="scamInput" className={`mb-2 ms-1 block text-[14px] font-extrabold ${NAVY}`}>
            {t.scInputLabel}
          </label>
          <textarea
            id="scamInput"
            value={input}
            onChange={e => setInput(e.target.value)}
            rows={4}
            placeholder={t.scPlaceholder}
            className="w-full rounded-[13px] border-[1.5px] border-[#b9a9d8] bg-white px-[18px] py-3 text-[17px] leading-relaxed text-[#1c1131] outline-none placeholder:text-slate-400 focus:border-violet-600 focus:shadow-[0_0_0_4px_rgba(124,58,237,.08)]"
          />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={run}
              disabled={!input.trim() || busy}
              className="h-[54px] cursor-pointer rounded-[12px] border-0 bg-gradient-to-b from-violet-600 to-violet-800 px-8 text-[17px] font-extrabold text-white shadow-[0_8px_18px_rgba(124,58,237,.2)] disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:shadow-none"
            >
              {busy && !stage ? t.scChecking : t.scCheck}
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex h-[54px] cursor-pointer items-center gap-2 rounded-[12px] border-[1.5px] border-violet-300 bg-white px-6 text-[16px] font-extrabold text-violet-700 hover:border-violet-500 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              <Video className="h-5 w-5" />
              {t.scUpload}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,video/mp4,video/webm" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) runMedia(f) }} />
            {error && <p className="m-0 text-[15px] text-[#a13b31]">{error}</p>}
          </div>
        </section>

        {busy && stage && (
          <ProcessingPanel
            stage={stage}
            uploading={stage === t.scStageUpload || stage === t.scStageVideo || stage === t.scStageAudio}
            notes={stage === t.scStageLink ? t.scLinkNotes : stage === t.scStageText ? t.scTextNotes : t.scNotes}
            site={resolveSite()}
          />
        )}

        {incomplete && (
          <section role="status" className="mb-5 grid grid-cols-[64px_1fr] items-start gap-[18px] rounded-[18px] border border-[#efddb2] bg-[#fff9ee] px-[18px] py-[22px] sm:px-7 sm:py-[26px]">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-[#d08a12] text-white">
              <HelpCircle className="h-8 w-8" strokeWidth={2.5} />
            </div>
            <p className="m-0 self-center text-[17px] leading-[1.6] text-[#3a3556]">{t.scErrIncomplete}</p>
          </section>
        )}
        {incomplete && <PartnerVerifyBox />}

        {thin && (
          <section role="status" className="mb-5 grid grid-cols-[64px_1fr] items-start gap-[18px] rounded-[18px] border border-violet-200 bg-[#f9f7ff] px-[18px] py-[22px] sm:px-7 sm:py-[26px]">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-violet-600 text-white">
              <HelpCircle className="h-8 w-8" strokeWidth={2.5} />
            </div>
            <p className="m-0 self-center text-[17px] leading-[1.6] text-[#3a3556]">{t.scThinInput}</p>
          </section>
        )}

        {result && (() => {
          const v = VERDICT[result.verdict]
          const flags = [...result.flags].sort((a, b) => ({ high: 0, medium: 1, low: 2 })[a.severity] - ({ high: 0, medium: 1, low: 2 })[b.severity])
          return (
            <>
              {/* Verdict */}
              <section className={`mb-5 grid grid-cols-[64px_1fr] items-start gap-[18px] rounded-[18px] border px-[18px] py-[22px] sm:px-7 sm:py-[26px] ${v.card}`}>
                <div className={`grid h-16 w-16 place-items-center rounded-full text-white ${v.icon}`}>
                  <v.Icon className="h-8 w-8" strokeWidth={3} />
                </div>
                <div className="min-w-0">
                  <div className={`text-[15px] font-extrabold ${result.verdict === 'DANGEROUS' ? 'text-[#a13b31]' : result.verdict === 'CAUTION' ? 'text-[#9a6500]' : 'text-[#2f8d36]'}`}>
                    {t[v.label] as string}
                  </div>
                  <h2 className={`m-0 mb-[7px] mt-1 text-[22px] font-bold leading-[1.25] sm:text-[26px] ${NAVY}`}>{result.headline}</h2>
                  <p className="m-0 text-[16px] leading-[1.6] text-[#3a3556]">{result.explanation}</p>
                </div>
              </section>

              {/* What we looked at: restate the case so the reader can see we
                  understood what they sent, then the check-by-check ledger. */}
              {result.observation && result.observation.checks.length > 0 && (
                <section className="mb-5 rounded-[18px] border border-[#e6e2f3] bg-white p-5 shadow-[0_10px_30px_rgba(56,23,112,.07)] sm:p-7">
                  <h3 className={`m-0 mb-3 text-[19px] font-bold ${NAVY}`}>{t.scWhatWeLookedAt}</h3>
                  <p className="m-0 mb-5 text-[16px] leading-[1.6] text-[#3a3556]">{result.observation.summary}</p>
                  <ul className="m-0 list-none space-y-3 p-0">
                    {result.observation.checks.map(c => {
                      const s = CHECK_STATUS[c.status]
                      const isBrand = c.key === 'brand'
                      const ref = isBrand ? result.observation?.brandVerification?.reference : null
                      return (
                        <li key={c.key} className="flex gap-3">
                          <span aria-hidden className={`mt-[2px] grid h-6 w-6 shrink-0 place-items-center rounded-full ${s.badge}`}>
                            <s.Icon className="h-3.5 w-3.5" strokeWidth={3} />
                          </span>
                          <div className="min-w-0">
                            <p className={`m-0 text-[16px] font-bold ${NAVY}`}>{c.title}</p>
                            <p className="m-0 mt-0.5 text-[15px] leading-[1.55] text-[#4a4762]">
                              {c.detail}
                              {ref && (
                                <a href={ref} target="_blank" rel="noopener noreferrer" className="ms-2 inline-flex items-center gap-1 font-bold text-violet-700 no-underline hover:underline">
                                  {t.scPublicRecord} <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </p>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )}

              {/* Why */}
              {flags.length > 0 && (
                <section className="mb-5 rounded-[18px] border border-[#e6e2f3] bg-white p-5 shadow-[0_10px_30px_rgba(56,23,112,.07)] sm:p-7">
                  <h3 className={`m-0 mb-3 text-[19px] font-bold ${NAVY}`}>{t.scWhatWeNoticed}</h3>
                  <ul className="m-0 list-none space-y-4 p-0">
                    {flags.map((f, i) => (
                      <li key={i} className="flex gap-3">
                        <span aria-hidden className={`mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full ${f.severity === 'high' ? 'bg-[#c9382a]' : f.severity === 'medium' ? 'bg-[#d08a12]' : 'bg-slate-300'}`} />
                        <div>
                          <p className={`m-0 text-[16px] font-bold ${NAVY}`}>{f.title}</p>
                          <p className="m-0 mt-0.5 text-[15px] leading-[1.55] text-[#4a4762]">{f.simplified || f.explanation}</p>
                          {f.evidence && <p className="m-0 mt-1 rounded bg-slate-50 px-2 py-1 text-[15px] italic text-slate-600">“{f.evidence}”</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* What to do */}
              {result.actions.length > 0 && (
                <section className="mb-5 rounded-[18px] border border-violet-200 bg-[#f9f7ff] p-5 sm:p-7">
                  <h3 className={`m-0 mb-3 text-[19px] font-bold ${NAVY}`}>{t.scWhatToDo}</h3>
                  <ol className="m-0 list-none space-y-3 p-0">
                    {result.actions.map(a => (
                      <li key={a.step} className="flex items-start gap-3">
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[13px] font-black text-violet-700">{a.step}</span>
                        <span className="text-[16px] leading-[1.6] text-[#2c2a45]">
                          {a.text}
                          {a.buttonUrl && (
                            <a href={a.buttonUrl} target="_blank" rel="noopener noreferrer" className="ms-2 inline-flex items-center gap-1 font-bold text-violet-700 no-underline hover:underline">
                              {a.buttonLabel ?? t.scOpen} <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              <PartnerVerifyBox />
            </>
          )
        })()}

        <p className="mx-auto mb-0 mt-[18px] max-w-[860px] text-center text-[15px] leading-[1.55] text-[#6b6590]">
          {t.scFooter}
        </p>
      </main>
    </div>
  )
}

/**
 * "Check with <partner>": the partner's own official contacts under every
 * result on a financial-institution face (and under an incomplete check).
 * Display only; sourced from lib/community-partners.ts.
 */
function PartnerVerifyBox() {
  const v = COMMUNITY_PARTNER?.verifyContact
  if (!v) return null
  return (
    <section aria-labelledby="partner-verify" className="mb-5 rounded-[18px] border-2 border-violet-300 bg-white p-5 sm:p-7">
      <h3 id="partner-verify" className={`m-0 mb-2 text-[19px] font-bold ${NAVY}`}>{v.heading}</h3>
      <p className="m-0 mb-4 text-[16px] leading-[1.6] text-[#3a3556]">{v.body}</p>
      <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-3">
        {v.phones.map(p => (
          <li key={p.tel} className="rounded-[12px] bg-[#f9f7ff] px-4 py-3">
            <span className="block text-[15px] leading-snug text-[#4a4762]">{p.label}</span>
            <a href={`tel:${p.tel}`} className="mt-1 inline-flex items-center gap-2 whitespace-nowrap text-[18px] font-bold text-violet-700 no-underline hover:underline">
              <Phone className="h-4 w-4" /> {p.phone}
            </a>
          </li>
        ))}
      </ul>
      <a href={v.website} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-[16px] font-bold text-violet-700 no-underline hover:underline">
        {v.websiteLabel} <ExternalLink className="h-4 w-4" />
      </a>
    </section>
  )
}

/**
 * Analyzing state, shown for every check (link, message, picture, audio,
 * video) on every face: a brand mark with a spinning ring, the current
 * stage, and rotating subtitles so a 30-second link check or a two-minute
 * video analysis never looks stalled. The GranGuard face (and its community
 * partners) gets the G mark; other tenants get the checker's shield so the
 * panel follows their theme colours without needing a raster logo.
 */
function ProcessingPanel({ stage, uploading, notes, site }: { stage: string; uploading: boolean; notes: string[]; site: ReturnType<typeof resolveSite> }) {
  const t = useUi()
  const panelRef = useRef<HTMLElement>(null)
  const [noteIdx, setNoteIdx] = useState(0)
  useEffect(() => {
    setNoteIdx(0)
    const timer = setInterval(() => setNoteIdx(i => (i + 1) % notes.length), 3200)
    return () => clearInterval(timer)
  }, [notes])
  // On a phone the input card fills the screen, so the panel would appear
  // below the fold and the tap would look like it did nothing.
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])
  const showG = site === 'granguard'
  return (
    <section ref={panelRef} role="status" aria-live="polite" className="mb-5 rounded-[18px] border border-violet-200 bg-[#f9f7ff] px-6 py-12 text-center">
      <div className="relative mx-auto h-24 w-24">
        <div
          aria-hidden
          className="absolute inset-0 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600"
          style={{ animationDuration: '1.1s' }}
        />
        <div className="absolute inset-[10px] flex items-center justify-center rounded-full bg-white shadow-sm">
          {showG
            ? <img src="/g_logo_violet.png" alt="" className="h-11 w-11 object-contain" />
            : (
              <div aria-hidden className={`grid h-11 w-10 place-items-center bg-gradient-to-br from-violet-500 to-violet-800 text-white ${SHIELD}`}>
                <Search className="h-5 w-5 -translate-y-[1px]" strokeWidth={2.5} />
              </div>
            )}
        </div>
      </div>
      <p className="mb-0 mt-6 text-[20px] font-bold text-[#221a3d]">{stage}</p>
      <p className="mb-0 mt-2 min-h-[24px] text-[15px] text-[#6b6590]">
        {uploading ? t.scHangTight : notes[noteIdx]}
      </p>
    </section>
  )
}